<template>
  <div v-show="isSearchOpen" :class="$style.searchFormWrap">
    <form action="/listing" :class="$style.searchForm">
      <input ref="modalSearchRef" type="search" name="q" tabindex="1" placeholder="何をお探しですか？" :value="query" :class="$style.searchQuery" />
      <button type="submit" :class="$style.searchSubmit">
        <SvgIcon :svg="SearchSvg" width="20" aria-label="検索" />
      </button>
    </form>
    <div :class="$style.modalClose" @click.stop="onClickCloseSearchButton">
      <i class="icon-cross1"></i>
    </div>
  </div>
  <div :class="$style.categoryWrap">
    <div v-if="isSearchOpen && !currentCategory" :class="$style.recommend">
      <p :class="$style.recommendTitle">おすすめ</p>
      <HeaderRecommend :api-key="apiKey" />
    </div>
    <template v-if="currentModal === 'category'">
      <div :class="$style.categoryTitleWrap">
        <div
          v-if="currentCategory"
          :class="$style.subCategoriesClose"
          @click.stop="
            () => {
              currentCategory = '';
            }
          "
        >
          <i class="icon-arrow-left"></i>
        </div>
        <p>{{ currentCategory ? cats.find((cat) => cat.slug === currentCategory)!.name : 'カテゴリーから探す' }}</p>
        <div v-if="!isSearchOpen" :class="$style.modalClose" @click.stop="onClickCloseModalButton">
          <i class="icon-cross1"></i>
        </div>
      </div>
      <ul :class="$style.categories">
        <li
          v-for="cat in cats"
          :key="cat.slug"
          :class="[currentCategory === cat.slug && $style.isActive]"
          :data-category="cat.slug"
          @mouseenter="
            () => {
              if (isPc) {
                onMouseEnterCategoryPC(cat.slug);
              }
            }
          "
          @mouseleave="
            () => {
              if (isPc) {
                onMouseLeaveCategoryPC();
              }
            }
          "
          @click.stop="
            () => {
              onClickCategory(cat.slug);
            }
          "
        >
          <template v-if="cat.slug !== 'others'">
            <span>
              {{ cat.name }}
            </span>
            <div :class="[$style.subCategories, isSearchOpen && $style.isSearchOpen]">
              <ul>
                <li>
                  <a :href="`/category/${cat.slug}`"> 全ての{{ cat.name }} </a>
                </li>
                <li v-for="subcat in subcats(cat.categoryId)" :key="subcat.slug" :class="`category-${subcat.slug}`">
                  <a :href="`/category/${cat.slug}/${subcat.slug}`">{{ subcat.name }}</a>
                </li>
              </ul>
            </div>
          </template>
          <template v-else>
            <a :href="`/category/${cat.slug}`">{{ cat.name }}</a>
          </template>
        </li>
        <li data-category="vintage"><a :href="`/listing?vintage=on`">アンティーク・ヴィンテージ</a></li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMQ } from '@/js/lib/helper/media-query';
import { categories, type CategoryData } from '@/js/data/category';
import SvgIcon from '@/js/components/molecules/SvgIcon.vue';
import SearchSvg from '@/svgs/vue/search.svg';
import HeaderRecommend from './HeaderRecommend.vue';

defineProps<{
  cats: CategoryData[];
  apiKey: string;
}>();
const emit = defineEmits<{
  (e: 'close'): void;
}>();
const query = defineModel<string>('query');
const currentModal = defineModel<string>('currentModal');
const currentCategory = defineModel<string>('currentCategory');
const isSearchOpen = defineModel<boolean>('isSearchOpen');
const modalSearchRef = defineModel<HTMLElement>('modalSearchRef');
const { isPc } = useMQ();
const sethover = ref<number | undefined>(undefined);
const subcats = computed(() => (categoryId: number) => categories({ parentCategoryId: categoryId }));
const onClickCloseSearchButton = () => {
  document.querySelector('body')?.classList.remove('header-menu-open');
  currentModal.value = '';
  currentCategory.value = '';
  isSearchOpen.value = false;
};
const onClickCloseModalButton = () => {
  emit('close');
};
const onMouseEnterCategoryPC = (slug: string) => {
  sethover.value = window.setTimeout(() => {
    currentCategory.value = slug;
  }, 300);
};
const onMouseLeaveCategoryPC = () => {
  clearTimeout(sethover.value);
};
const onClickCategory = (slug: string) => {
  currentCategory.value = slug;
};
</script>

<style module lang="scss">
.modalClose {
  > i {
    cursor: pointer;
    display: block;
    font-size: 24px;
    margin: -5px -10px -5px 0;
    padding: 12px;
  }

  :global(.webview) & {
    display: none;
  }
}

.searchForm {
  position: relative;
  width: 400px;

  @media (--sp) {
    width: auto;
  }

  .searchQuery {
    border: 1px solid var(--border-color);
    border-radius: 4px;
    height: 38px;
    margin: 0;
    padding: 0 36px 0 8px;
    width: 100%;

    &:focus {
      outline: none;
    }
  }

  .searchSubmit {
    align-items: center;
    border: none;
    display: flex;
    height: 38px;
    padding: 0 10px;
    position: absolute;
    right: 0;
    top: 0;

    svg {
      fill: #999;
    }
  }
}

.searchFormWrap {
  background-color: var(--color-white); // アプリ向け
  display: none;
  justify-content: flex-end;
  padding: 10px;

  @media (--sp) {
    border-bottom: 1px solid var(--border-color);
    display: flex;
  }

  .searchForm {
    flex: 1;
  }
}

.categoryWrap {
  display: flex;
  flex-direction: column;

  @media (--sp) {
    overflow-y: scroll;
  }
}

.recommend {
  border-bottom: 1px solid var(--border-color);
  padding: 12px 16px;
}

.recommendTitle {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: center;
}

.categoryTitleWrap {
  background-color: var(--color-white);
  display: none;
  justify-content: space-between;
  padding: 12px;
  position: sticky;
  text-align: center;
  top: 0;
  z-index: 1;

  @media (--sp) {
    display: block;
  }

  p {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0;
  }

  .subCategoriesClose {
    cursor: pointer;
    left: 2px;
    position: absolute;
    top: 6px;

    > i {
      font-size: 36px;
    }
  }

  .modalClose {
    position: absolute;
    right: 0;
    top: 0;

    > i {
      margin: 0;
    }
  }
}

.categories {
  background-color: var(--color-white);
  padding: 5px 0 5px 5px;
  width: 320px;

  @media (--sp) {
    flex: 1;
    margin-top: 0;
    padding: 0 0 5px 5px;
    position: relative;
    width: 100%;
  }

  &:has(.isActive) {
    @media (--sp) {
      overflow-y: hidden;
    }
  }

  > li {
    @media (--sp) {
      position: static;
    }

    > a,
    > span {
      color: inherit;
      cursor: pointer;
      display: block;
      height: 52px;
      line-height: 26px;
      padding: 13px 13px 13px 55px;
      position: relative;
      text-decoration: none;

      @media (--sp) {
        font-size: 16px;
        padding-left: 57px;
      }

      &:hover {
        background: var(--color-gray100);
      }

      &::before {
        background-repeat: no-repeat;
        background-size: 40px;
        border: 1px solid var(--border-color);
        content: ' ';
        height: 42px;
        left: 5px;
        position: absolute;
        top: 5px;
        width: 42px;
      }

      &::after {
        color: var(--color-black);
        content: $icon-arrow-right;
        font-family: $font-icon;
        font-size: 28px;
        line-height: 1;
        position: absolute;
        right: 5px;
        top: 10px;

        @media (--sp) {
          right: 10px;
        }
      }
    }

    &[data-category='accessories'] > span::before {
      background-image: url('@/images/icons/category-accessories.jpg');
    }

    &[data-category='clothing'] > span::before {
      background-image: url('@/images/icons/category-clothing.jpg');
    }

    &[data-category='bag_case'] > span::before {
      background-image: url('@/images/icons/category-bag_case.jpg');
    }

    &[data-category='kitchenware'] > span::before {
      background-image: url('@/images/icons/category-kitchenware.jpg');
    }

    &[data-category='houseware'] > span::before {
      background-image: url('@/images/icons/category-houseware.jpg');
    }

    &[data-category='miscellaneous-goods'] > span::before {
      background-image: url('@/images/icons/category-miscellaneous-goods.jpg');
    }

    &[data-category='art'] > span::before {
      background-image: url('@/images/icons/category-art.jpg');
    }

    &[data-category='kids_baby'] > span::before {
      background-image: url('@/images/icons/category-kids_baby.jpg');
    }

    &[data-category='doll_toy'] > span::before {
      background-image: url('@/images/icons/category-doll_toy.jpg');
    }

    &[data-category='tools_supplies'] > span::before {
      background-image: url('@/images/icons/category-tools_supplies.jpg');
    }

    &[data-category='food'] > span::before {
      background-image: url('@/images/icons/category-food.jpg');
    }

    &[data-category='others'] > a {
      &::before {
        background-image: url('@/images/icons/category-others.jpg');
      }

      &::after {
        display: none;
      }
    }

    &[data-category='vintage'] > a {
      &::before {
        background-image: url('@/images/icons/category-vintage.jpg');
      }

      &::after {
        display: none;
      }
    }

    .subCategories {
      background-color: var(--color-white);
      display: none;
      height: 686px;
      left: 100%;
      padding: 10px;
      position: absolute;
      top: 0;
      width: 300px;

      @media (--sp) {
        bottom: 0;
        display: block;
        height: calc(100vh - 48px);
        height: calc(100dvh - 48px);
        overflow-y: auto;
        position: fixed;
        top: initial;
        transition: left 0.2s;
        width: 100% !important;
        z-index: 1;

        &.isSearchOpen {
          height: calc(100vh - 107px);
          height: calc(100dvh - 107px);
        }
      }

      a {
        display: block;
        line-height: 26px;
        padding: 7px 6px;

        @media (--sp) {
          font-size: 16px;
        }

        &:hover {
          background: var(--color-gray100);
          text-decoration: none;
        }
      }

      > ul {
        display: flex;
        flex-flow: column wrap;
        height: 100%;
        margin-top: 0;

        @media (--sp) {
          flex-flow: column;
        }

        > li {
          &:first-child {
            @media (--pc) {
              font-weight: bold;
            }
          }

          &:last-child {
            @media (--sp) {
              padding-bottom: 10px;
            }
          }
        }
      }
    }

    &[data-category='accessories'] .subCategories,
    &[data-category='bag_case'] .subCategories,
    &[data-category='houseware'] .subCategories,
    &[data-category='miscellaneous-goods'] .subCategories,
    &[data-category='food'] .subCategories {
      width: 520px;
    }

    &:hover .subCategories {
      @media (--pc) {
        display: block;
      }
    }

    &.isActive .subCategories {
      @media (--sp) {
        left: 0;
      }
    }
  }
}
</style>
